import { useEffect } from "react";
import { isRouteSelected, isVesselExpanded, isVoyageExpanded } from "@/features/routeTree/state/VesselTreeSelectors.jsx";
import { treeSelectionActions } from "@/features/routeTree/state/VesselTreeState.jsx";

/**
 * useRouteTreeSyncEffects
 *
 * Purpose:
 * - Keep tree selection/expansion in sync with external events.
 * - Currently handles import-selection hints and draft route auto-selection.
 */
export default function useRouteTreeSyncEffects({
  onImportSelectionHint,
  treeState,
  treeDispatch,
  upsertVessel,
  upsertVoyageForVessel,
  archiveOtherActiveVoyages,
  archivePlannedRoutesForVoyage,
  upsertRouteForVoyage,
  loadVoyagesForVessel,
  loadRoutesForVoyage,
  setVesselsExpanded,
  lastAppliedImportRouteIdRef,
  draftRoutes,
  draftAutoAppliedRef,
  vessels,
  markedVesselNames,
  markVesselAsActive,
}) {
  useEffect(() => {
    const hint = onImportSelectionHint;
    if (!hint) return;

    const routeId = Number(hint?.routeId);
    const voyageId = Number(hint?.voyageId);
    const vesselId = Number(hint?.vesselId);
    if (
      !Number.isFinite(routeId) ||
      !Number.isFinite(voyageId) ||
      !Number.isFinite(vesselId)
    ) {
      return;
    }
    if (lastAppliedImportRouteIdRef.current === routeId) return;
    lastAppliedImportRouteIdRef.current = routeId;
    setVesselsExpanded(true);

    const applyImportHint = async () => {
      const vessel = {
        id: vesselId,
        name: hint?.vesselName || `Vessel ${vesselId}`,
      };

      const voyage = {
        id: voyageId,
        vesselId,
        name: hint?.voyageName || `Voyage ${voyageId}`,
        type: "planned",
        state: "active",
        active: true,
        timestamp: hint?.timestamp || null,
      };
      const routeState = hint?.state || "suggested";
      const route = {
        id: routeId,
        voyageId,
        name: hint?.routeName || `Route ${routeId}`,
        type: hint?.routeType || "planned",
        state: routeState,
        active: routeState === "active",
        timestamp: hint?.timestamp || null,
        summary: hint?.summary ?? null,
        optimisedFor: hint?.optimisedFor ?? null,
      };

      // Optimistic seed into the store — the loaders below overwrite with
      // the DB truth once the refetches land.
      upsertVessel(vessel);
      upsertVoyageForVessel(vesselId, voyage);
      if (hint?.voyageActivated) {
        archiveOtherActiveVoyages(vesselId, voyageId);
      }
      upsertRouteForVoyage(voyageId, route);

      if (!isVesselExpanded(treeState, vesselId)) {
        treeDispatch(treeSelectionActions.ensureVesselExpanded(vesselId));
      }
      if (!isVoyageExpanded(treeState, voyageId)) {
        treeDispatch(treeSelectionActions.ensureVoyageExpanded(voyageId));
      }
      if (!isRouteSelected(treeState, routeId)) {
        treeDispatch(
          treeSelectionActions.toggleRouteSelected(vesselId, voyageId, routeId),
        );
      }

      // Force a refresh so the archived sibling voyage reflects the DB
      // (the loader SETs the store itself). The just-created voyage can be
      // missing from the response (read-after-write lag) — its SET would
      // clobber the optimistic seed, so re-seed it.
      const fetchedVoyages = await loadVoyagesForVessel(vesselId, null, {
        forceRefresh: true,
      });
      const voyageFetched = (fetchedVoyages ?? []).some(
        (fetched) => Number(fetched?.id) === voyageId,
      );
      if (!voyageFetched) {
        upsertVoyageForVessel(vesselId, voyage);
      }
      if (hint?.voyageActivated) {
        // The DB archive cascades to the demoted voyages' routes — mirror it
        // on the loaded rows so their badges don't stay active/suggested.
        for (const fetchedVoyage of fetchedVoyages ?? []) {
          const fetchedVoyageId = Number(fetchedVoyage?.id);
          if (!Number.isFinite(fetchedVoyageId) || fetchedVoyageId === voyageId)
            continue;
          if (String(fetchedVoyage?.state ?? "").toLowerCase() !== "archived")
            continue;
          archivePlannedRoutesForVoyage?.(fetchedVoyageId);
        }
      }

      // Force a refresh so saved route + demoted siblings reflect the DB.
      // Same lag guard: if the response misses the imported route, its SET
      // clobbered the seeded relation — re-seed so the row isn't orphaned.
      const fetchedRoutes = await loadRoutesForVoyage(voyageId, {
        forceRefresh: true,
      });
      const routeFetched = (fetchedRoutes ?? []).some(
        (fetched) => Number(fetched?.id) === routeId,
      );
      if (!routeFetched) {
        upsertRouteForVoyage(voyageId, route);
      }
    };

    applyImportHint();
  }, [
    onImportSelectionHint,
    treeState,
    treeDispatch,
    upsertVessel,
    upsertVoyageForVessel,
    archiveOtherActiveVoyages,
    archivePlannedRoutesForVoyage,
    upsertRouteForVoyage,
    loadVoyagesForVessel,
    loadRoutesForVoyage,
    setVesselsExpanded,
    lastAppliedImportRouteIdRef,
  ]);

  useEffect(() => {
    const liveKeys = new Set();
    const applied = draftAutoAppliedRef.current;

    for (const draft of draftRoutes ?? []) {
      const routeId = draft?.id;
      const vesselId = Number(draft?.vesselId);
      const voyageId = Number(draft?.voyageId);
      if (!routeId || !Number.isFinite(vesselId) || !Number.isFinite(voyageId)) {
        continue;
      }
      liveKeys.add(routeId);

      if (applied.has(routeId)) continue;
      if (!vessels.some((v) => Number(v?.id) === vesselId)) continue;

      applied.add(routeId);
      const applyDraftSelection = async () => {
        await loadVoyagesForVessel(vesselId);
        await loadRoutesForVoyage(voyageId);

        // Mark first — markVesselAsActive clears selections, which would wipe the toggle below.
        const vessel = vessels.find((v) => Number(v?.id) === vesselId);
        if (vessel) {
          const vesselName = vessel?.name ?? "";
          const alreadyMarked = (markedVesselNames ?? []).some(
            (entry) => (entry?.vesselName ?? "") === vesselName,
          );
          if (!alreadyMarked) {
            markVesselAsActive(vessel, { recenter: false });
          }
        }

        if (!isRouteSelected(treeState, routeId)) {
          treeDispatch(
            treeSelectionActions.toggleRouteSelected(vesselId, voyageId, routeId),
          );
        }
        setVesselsExpanded(true);
        if (!isVesselExpanded(treeState, vesselId)) {
          treeDispatch(treeSelectionActions.ensureVesselExpanded(vesselId));
        }
        if (!isVoyageExpanded(treeState, voyageId)) {
          treeDispatch(treeSelectionActions.ensureVoyageExpanded(voyageId));
        }
      };

      applyDraftSelection();
    }

    // Re-arm vanished drafts so a future one with the same id applies again.
    for (const key of [...applied]) {
      if (!liveKeys.has(key)) applied.delete(key);
    }
  }, [
    draftRoutes,
    treeState,
    treeDispatch,
    vessels,
    markedVesselNames,
    markVesselAsActive,
    draftAutoAppliedRef,
    loadVoyagesForVessel,
    loadRoutesForVoyage,
    setVesselsExpanded,
  ]);
}
